import { eventKeys } from "@/lib/eventKeys";
import { queryKeys } from "@/lib/queryKeys";
import userTodoMapper from "@/mappers/userTodoMapper";
import { TodoItem, useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import useGetEventBus from "./useGetEventBus";
import useFetchErrorNotifier from "./utils/useFetchErrorNotifier";

// Fetches todos for the Smart Todos dashboard widget.
// Fetch/event logic is mirrored in ToDoScreen.tsx (mobile).
const useGetDashboardTodos = () => {
    const { userManager } = useSdk();
    const notifyFetchError = useFetchErrorNotifier();
    const queryClient = useQueryClient();
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const todos = useQuery({
        queryKey: queryKeys.dashboardTodos(),
        queryFn: async () => {
            try {
                const userTodos = await userManager.getUserTodos(true);

                // Le dashboard n'affiche que les todos non terminés : getUserTodos(true)
                // force un fetch frais mais renvoie aussi les todos done, qui fausseraient
                // le compteur du titre (todos.length). On filtre ici, cohérent avec le
                // payload de l'event 'user-todos-updated' (lui aussi filtré !done).
                const mappedUserTodos = userTodos
                    .filter((todo) => !todo.done)
                    .map(userTodoMapper);

                return mappedUserTodos;
            } catch (error) {
                notifyFetchError({
                    queryKey: queryKeys.dashboardTodos(),
                    titleTranslationKey: "errors.getTodos",
                });

                // Sans le throw, TanStack rejette sur « data is undefined » et perd la cause.
                throw error;
            }
        },
    });

    const handleTodosUpdated = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.dashboardTodos(),
            });
            timeoutRef.current = null;
        }, 2300);
    };

    const { off: offTodosUpdated } = useGetEventBus<TodoItem[]>(
        eventKeys.todos.updated,
        handleTodosUpdated,
    );

    useEffect(
        () => () => {
            offTodosUpdated(eventKeys.todos.updated, handleTodosUpdated);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        },
        [],
    );

    return todos;
};

export default useGetDashboardTodos;
